/**
 * Brand-slug helpers shared between the brand-creation surfaces
 * (`app/b/new/_components/new-brand-form.tsx` and
 * `components/client-switcher.tsx::CreateBrandDialog`).
 *
 * `'new'` is reserved because `/b/new` is the create-brand URL — a
 * brand whose slug were `'new'` would collide. Slugify normalises that
 * case to `'new-1'` instead. The backend may still reject collisions and
 * suggest a different slug; this only handles the URL-namespace conflict.
 */

const RESERVED_SLUGS = new Set(['new'])

function stableUnicodeSuffix(input: string): string {
  // FNV-1a over Unicode code points gives non-Latin-only names a stable,
  // URL-safe suffix without pulling transliteration rules into the routing
  // contract. Math.imul keeps the calculation in unsigned 32-bit space.
  let hash = 0x811c9dc5
  for (const char of input.normalize('NFKC')) {
    hash ^= char.codePointAt(0) ?? 0
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(36).padStart(7, '0')
}

export function slugifyBrandName(input: string): string {
  // Order matters:
  //   1. lowercase + trim outer whitespace
  //   2. replace non-[a-z0-9-] runs with single `-`
  //   3. collapse multi-dash runs BEFORE slicing so the slice can't land
  //      mid-run and re-leave a multi-dash tail
  //   4. slice to the 63-char DNS-label limit
  //   5. strip leading/trailing dashes AFTER the slice (slicing at the
  //      63rd char can land right after a hyphen and re-introduce a
  //      trailing dash, which would also pass the backend SLUG_PATTERN
  //      regex — strip here as the single source of truth)
  const normalized = input.trim()
  const out = normalized
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 63)
    .replace(/^-+|-+$/g, '')
  const usable =
    out || (normalized ? `brand-${stableUnicodeSuffix(normalized)}` : '')
  return RESERVED_SLUGS.has(usable) ? `${usable}-1` : usable
}
